/**
 * hw-ppe-multibuy.js
 * Drives the PPE product page multibuy selector:
 * - Tier active state based on cart quantity + selected quantity.
 * - Dynamic main price and subtotal updates.
 * - Multi-item add-to-cart via /cart/add.js.
 */

if (!customElements.get('hw-ppe-multibuy')) {
  class HwPpeMultibuy extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.productId = Number(this.dataset.productId);
      this.sizeOptionIndex = Number(this.dataset.sizeOptionIndex);
      this.unitLabel = this.dataset.unitLabel || 'case';
      this.unitLabelPlural = this.dataset.unitLabelPlural || 'cases';
      this.basePrice = Number(this.dataset.basePrice) || 0;
      this.taxRate = Number(this.dataset.taxRate) || 0;
      this.vatEnabled = this.dataset.vatEnabled === 'true';
      this.chooseText = this.dataset.chooseText || 'Choose quantities';
      this.addText = this.dataset.addText || 'Add to basket';

      this.tierEls = Array.from(this.querySelectorAll('[data-tier]'));
      this.qtyInputs = Array.from(this.querySelectorAll('[data-qty-input]'));
      this.submitBtn = this.querySelector('[data-submit]');
      this.summaryCount = this.querySelector('[data-summary-count]');
      this.summarySubtotal = this.querySelector('[data-summary-subtotal]');
      this.sizeGrid = this.querySelector('[data-size-grid]');

      this.cartQuantity = 0;
      this.activePrice = this.basePrice;

      try {
        this.currencyFormatter = new Intl.NumberFormat(Shopify.locale || 'en-GB', {
          style: 'currency',
          currency: Shopify.currency?.active || 'GBP'
        });
      } catch (e) {
        this.currencyFormatter = {
          format: (amount) => '£' + (amount / 100).toFixed(2)
        };
      }

      this.bindEvents();
      this.refreshCartState().then(() => this.updateUI());
    }

    bindEvents() {
      this.sizeGrid.addEventListener('click', (event) => {
        const minusBtn = event.target.closest('[data-qty-minus]');
        const plusBtn = event.target.closest('[data-qty-plus]');
        if (!minusBtn && !plusBtn) return;

        event.preventDefault();
        const input = (minusBtn || plusBtn).closest('[data-size-option]').querySelector('[data-qty-input]');
        if (!input) return;

        const step = plusBtn ? 1 : -1;
        const next = Math.max(0, (parseInt(input.value, 10) || 0) + step);
        if (next !== parseInt(input.value, 10)) {
          input.value = next;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      this.qtyInputs.forEach((input) => {
        input.addEventListener('change', () => {
          input.value = Math.max(0, parseInt(input.value, 10) || 0);
          this.updateMinusButtons();
          this.updateUI();
        });
        input.addEventListener('input', () => {
          this.updateMinusButtons();
          this.updateUI();
        });
      });

      this.submitBtn.addEventListener('click', () => this.submit());

      // Keep in sync with cart changes from anywhere in the theme.
      document.addEventListener('on:cart:add', () => this.refreshCartState().then(() => this.updateUI()));
      document.addEventListener('on:line-item:change', () => this.refreshCartState().then(() => this.updateUI()));
      document.addEventListener('on:cart:refresh', () => this.refreshCartState().then(() => this.updateUI()));
    }

    async refreshCartState() {
      try {
        const response = await fetch(`${theme.routes.cart}.js`, {
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) return;
        const cart = await response.json();
        this.cartQuantity = (cart.items || []).reduce((total, item) => {
          return item.product_id === this.productId ? total + item.quantity : total;
        }, 0);
      } catch (error) {
        console.error('HW PPE multibuy: failed to load cart', error);
        this.cartQuantity = 0;
      }
    }

    getSelectedQuantity() {
      return this.qtyInputs.reduce((total, input) => total + (parseInt(input.value, 10) || 0), 0);
    }

    getTotalQuantity() {
      return this.cartQuantity + this.getSelectedQuantity();
    }

    getActiveTier(totalQuantity) {
      if (totalQuantity <= 0) return null;

      for (const tier of this.tierEls) {
        const min = parseInt(tier.dataset.min, 10);
        const max = tier.dataset.max ? parseInt(tier.dataset.max, 10) : Infinity;
        if (totalQuantity >= min && totalQuantity <= max) {
          return tier;
        }
      }

      return this.tierEls[this.tierEls.length - 1] || null;
    }

    formatMoney(cents) {
      return this.currencyFormatter.format(cents / 100);
    }

    getUnitLabel(count) {
      return count === 1 ? this.unitLabel : this.unitLabelPlural;
    }

    updateMinusButtons() {
      this.qtyInputs.forEach((input) => {
        const minusBtn = input.closest('[data-size-option]').querySelector('[data-qty-minus]');
        if (minusBtn) {
          minusBtn.disabled = (parseInt(input.value, 10) || 0) <= 0;
        }
      });
    }

    updateUI() {
      const selectedQuantity = this.getSelectedQuantity();
      const totalQuantity = this.getTotalQuantity();
      const activeTier = this.getActiveTier(totalQuantity);
      this.activePrice = activeTier ? parseInt(activeTier.dataset.price, 10) : this.basePrice;

      // Tier active states.
      this.tierEls.forEach((tier) => {
        const isActive = tier === activeTier;
        tier.classList.toggle('is-active', isActive);
        tier.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      // Summary.
      if (this.summaryCount) {
        this.summaryCount.textContent = `${selectedQuantity} ${this.getUnitLabel(selectedQuantity)}`;
      }
      if (this.summarySubtotal) {
        const subtotal = selectedQuantity * this.activePrice;
        this.summarySubtotal.textContent = `Subtotal: ${this.formatMoney(subtotal)} ex VAT`;
      }

      // Submit button.
      if (this.submitBtn) {
        if (selectedQuantity === 0) {
          this.submitBtn.disabled = true;
          this.submitBtn.textContent = this.chooseText;
        } else {
          this.submitBtn.disabled = false;
          this.submitBtn.textContent = this.addText;
        }
      }

      this.updateMainPrice();
    }

    updateMainPrice() {
      const productInfo = this.closest('.product-info');
      if (!productInfo) return;
      const priceContainer = productInfo.querySelector('.product-info__price');
      if (!priceContainer) return;

      const exVatValue = priceContainer.querySelector('.price__current .js-value');
      if (exVatValue) {
        exVatValue.textContent = this.formatMoney(this.activePrice);
      }

      if (this.vatEnabled && this.taxRate) {
        const incVatSpan = priceContainer.querySelector('.price__current .js-value.text-xs');
        if (incVatSpan) {
          const incVatPrice = Math.round(this.activePrice * (1 + this.taxRate / 100));
          const label = incVatSpan.querySelector('.price__vat-label');
          const priceText = `${this.formatMoney(incVatPrice)} `;
          if (incVatSpan.firstChild && incVatSpan.firstChild.nodeType === Node.TEXT_NODE) {
            incVatSpan.firstChild.textContent = priceText;
          } else {
            incVatSpan.insertBefore(document.createTextNode(priceText), incVatSpan.firstChild);
          }
          if (!label) {
            const newLabel = document.createElement('span');
            newLabel.className = 'price__vat-label';
            newLabel.textContent = `(incl. VAT)`;
            incVatSpan.appendChild(newLabel);
          }
        }
      }
    }

    async submit() {
      const items = this.qtyInputs
        .map((input) => ({
          id: Number(input.dataset.variantId),
          quantity: parseInt(input.value, 10) || 0
        }))
        .filter((item) => item.quantity > 0 && item.id);

      if (items.length === 0) return;

      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData();
      items.forEach((item, index) => {
        formData.append(`items[${index}][id]`, item.id);
        formData.append(`items[${index}][quantity]`, item.quantity);
      });
      formData.append('sections_url', window.location.pathname);

      const cartDrawer = document.querySelector('cart-drawer');
      const sections = ['cart-icon-bubble'];
      if (cartDrawer) {
        sections.push(cartDrawer.closest('.shopify-section').id.replace('shopify-section-', ''));
      }
      formData.append('sections', sections.join(','));

      try {
        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });

        const data = await response.json();
        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Add to cart failed');
        }

        // Reset selected quantities after successful add.
        this.qtyInputs.forEach((input) => { input.value = 0; });
        this.updateMinusButtons();

        if (cartDrawer) {
          cartDrawer.renderContents(data, true);
        }

        // Refresh cart state and UI.
        await this.refreshCartState();
        this.updateUI();
      } catch (error) {
        console.error('HW PPE multibuy add error:', error);
        alert(error.message || theme.strings?.cartError || 'Could not add items to cart.');
      } finally {
        this.submitBtn.classList.remove('is-loading');
        this.submitBtn.removeAttribute('aria-disabled');
      }
    }
  }

  customElements.define('hw-ppe-multibuy', HwPpeMultibuy);
}
